import { motion } from 'framer-motion'
import { Brain, ArrowRight, Check, Sliders, Clock, MessageCircle } from 'lucide-react'
import { useState, useEffect } from 'react'

interface StepProps {
  onNext: () => void
}

const Step2 = ({ onNext }: StepProps) => {
  const [activePreference, setActivePreference] = useState(0)

  const preferences = [
    {
      icon: Clock,
      title: 'Work Schedule',
      description: 'Morning person, prefers focused work before noon',
      learned: ['Best time for meetings: 2-4 PM', 'Deep work hours: 8-11 AM', 'No meetings on Fridays']
    },
    {
      icon: MessageCircle,
      title: 'Communication Style',
      description: 'Concise and professional, but friendly',
      learned: ['Prefers bullet points', 'Signs off with "Best"', 'Uses technical terminology naturally']
    },
    {
      icon: Sliders,
      title: 'Task Preferences',
      description: 'Likes structured approaches with clear priorities',
      learned: ['Breaks large tasks into subtasks', 'Weekly planning on Sundays', 'Uses color-coding for priority']
    }
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePreference((prev) => (prev + 1) % preferences.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [preferences.length])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.6 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-6 shadow-xl shadow-primary-500/30"
      >
        <Brain className="w-10 h-10 text-white" />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-3xl md:text-4xl font-bold text-center mb-4"
      >
        Learning <span className="gradient-text">Your Preferences</span>
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-lg text-surface-600 dark:text-surface-400 text-center max-w-2xl mb-12"
      >
        Through natural interactions, your AI Twin builds a deep understanding of how you work,
        communicate, and make decisions.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="w-full max-w-4xl"
      >
        {/* Preference Selector */}
        <div className="flex justify-center gap-4 mb-8">
          {preferences.map((pref, index) => (
            <button
              key={pref.title}
              onClick={() => setActivePreference(index)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activePreference === index
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                  : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-700'
              }`}
            >
              <pref.icon className="w-4 h-4" />
              <span className="hidden sm:inline font-medium">{pref.title}</span>
            </button>
          ))}
        </div>

        {/* Preference Detail Card */}
        <motion.div
          key={activePreference}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="card p-8"
        >
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center flex-shrink-0">
              {(() => {
                const Icon = preferences[activePreference].icon
                return <Icon className="w-8 h-8 text-primary-500" />
              })()}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-2">
                {preferences[activePreference].title}
              </h3>
              <p className="text-surface-600 dark:text-surface-400 mb-4">
                {preferences[activePreference].description}
              </p>
              <div className="space-y-2">
                <p className="text-sm font-medium text-surface-500 dark:text-surface-500 mb-2">
                  What your AI Twin has learned:
                </p>
                {preferences[activePreference].learned.map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="flex items-center gap-2"
                  >
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <span className="text-surface-700 dark:text-surface-300">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Learning Progress Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/10 text-accent-600 dark:text-accent-400">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
              className="w-4 h-4 border-2 border-accent-500 border-t-transparent rounded-full"
            />
            <span className="text-sm font-medium">Continuously learning from your interactions</span>
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        onClick={onNext}
        className="btn-primary text-lg group mt-12"
      >
        Explore Capabilities
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </motion.button>
    </div>
  )
}

export default Step2
